import type { ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * RowActions — the actions at the end of a list-table row (Antonin, 13 Oct: "when there is 3 actions or less, have
 * full buttons instead of ellipsis").
 *
 *   ≤ 3 actions   each one is a 32 px icon button with an instant tooltip naming it (Experiment, Antonin 13 Oct: "try to
 *                 have just icons with tooltip in those tables"); destructive ones in red ink, last
 *   > 3 actions   one ⋯ button opens a menu of them all (destructive items red and last)
 *
 * The row's click (when the row opens something) never fires from here: every button stops propagation.
 */
export interface RowAction {
  label: string;
  onClick: () => void;
  /** Every row action carries one (Antonin, 13 Oct: "it was ok with icons … just it needs to be consistent"): a Lucide
   *  icon at `size-4` (16 px, standard stroke), the same in the button and in the ⋯ menu. */
  icon: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
}

export const ROW_ACTIONS_INLINE_MAX = 3;

/** A row's icon button: 32 px, the neutral secondary frame (white, grey-300 edge, grey hover), a 16 px icon. */
export const ROW_ICON_BUTTON =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--color-grey-300)] bg-surface-card transition-colors hover:border-[var(--color-grey-400)] hover:bg-grey-100 active:bg-grey-200 disabled:cursor-not-allowed disabled:opacity-50';

export function RowActions({ actions, label, className }: {
  actions: readonly RowAction[];
  /** What the row is, for the ⋯ button's accessible name ("Actions for Main store"). */
  label: string;
  className?: string;
}) {
  if (actions.length === 0) return null;
  // Destructive last, in both forms.
  const ordered = [...actions.filter((a) => !a.destructive), ...actions.filter((a) => a.destructive)];
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };

  if (ordered.length <= ROW_ACTIONS_INLINE_MAX) {
    return (
      <div className={cn('flex items-center justify-end gap-2', className)} data-row-actions="inline">
        {ordered.map((a) => (
          <Tooltip key={a.label}>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={a.label}
                  disabled={a.disabled}
                  onClick={stop(a.onClick)}
                  className={cn(ROW_ICON_BUTTON, a.destructive ? 'text-status-error' : 'text-text-secondary')}
                />
              }
            >
              {a.icon}
            </TooltipTrigger>
            <TooltipContent>{a.label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('flex justify-end', className)} data-row-actions="menu">
      <Popover>
        <PopoverTrigger
          render={
            <button
              type="button"
              aria-label={`Actions for ${label}`}
              onClick={(e) => e.stopPropagation()}
              className={cn(ROW_ICON_BUTTON, 'text-text-secondary')}
            />
          }
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-48 p-1">
          {ordered.map((a) => (
            <button
              key={a.label}
              type="button"
              disabled={a.disabled}
              onClick={stop(a.onClick)}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[length:var(--type-body-size)] leading-[var(--type-body-line)] hover:bg-grey-100 disabled:opacity-50',
                a.destructive ? 'text-status-error' : 'text-text-primary',
              )}
            >
              {a.icon}
              {a.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default RowActions;
